import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, CurrencyInr, Warning, Spinner, CalendarBlank, Minus, Plus, Tag } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const inr = n => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const fmt = iso => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" }); } catch { return iso; } };

export default function ExtendOffer() {
  const { token } = useParams();
  const [offer, setOffer]   = useState(null);
  const [error, setError]   = useState("");
  const [nights, setNights] = useState(1);
  const [paying, setPaying] = useState(false);
  const [done, setDone]     = useState(null);
  const [msg, setMsg]       = useState("");

  useEffect(() => {
    fetch(`${API}/book/extend/${token}`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || "Offer not found"); return r.json(); })
      .then(setOffer)
      .catch(e => setError(e.message));
  }, [token]);

  const reserveAndPay = async () => {
    setMsg(""); setPaying(true);
    try {
      const r = await fetch(`${API}/book/extend/${token}/reserve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nights }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Could not reserve these dates");
      const data = await r.json();
      if (!data.razorpay_order_id) throw new Error("Online payment isn't set up. Please contact your host.");
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Payment gateway failed to load. Please retry.");
      const options = {
        key: data.razorpay_key_id || RAZORPAY_KEY,
        amount: Math.round((data.total || 0) * 100),
        currency: "INR",
        order_id: data.razorpay_order_id,
        name: offer.property_name || "Your stay",
        description: `Extend stay · ${nights} night(s)`,
        prefill: { name: offer.guest_name || "" },
        theme: { color: "#f59e0b" },
        handler: async (resp) => {
          try {
            const c = await fetch(`${API}/book/extend/${token}/confirm-payment`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!c.ok) throw new Error((await c.json()).detail || "Verification failed");
            setDone({ check_out: data.check_out, total: data.total, nights });
          } catch (e) { setMsg(e.message || "Payment could not be verified."); }
          finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (e) { setMsg(e.message || "Something went wrong"); setPaying(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
        <Warning size={36} weight="fill" className="text-amber-500 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">{error}</p>
      </div>
    </div>
  );

  if (!offer) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Spinner size={28} className="animate-spin text-amber-500" />
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={36} weight="fill" className="text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Stay Extended!</h2>
        <p className="text-slate-500 text-sm mb-6">
          Your stay at <strong>{offer.property_name}</strong> is extended to {fmt(done.check_out)}. A confirmation has been sent.
        </p>
        <div className="bg-slate-50 rounded-2xl p-4 text-sm text-left space-y-2">
          <div className="flex justify-between"><span className="text-slate-500">New checkout</span><span className="font-semibold">{fmt(done.check_out)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Extra nights</span><span className="font-semibold">{done.nights}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="font-bold text-amber-600">{inr(done.total)}</span></div>
        </div>
      </motion.div>
    </div>
  );

  const unavailable = offer.expired || (offer.max_nights || 0) < 1;
  const maxN = Math.max(1, offer.max_nights || 1);
  const nn = Math.min(nights, maxN);
  const origTotal = (offer.base_rate || 0) * nn;
  const subtotal  = (offer.nightly_rate || 0) * nn;
  const gst       = Math.round(subtotal * (offer.gst_pct || 0) / 100);
  const total     = subtotal + gst;
  const savings    = Math.max(0, origTotal - subtotal);
  const checkout  = addDays(offer.check_in, nn);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 max-w-md w-full space-y-5">
        <div className="text-center">
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 mb-2">
            <Tag size={11} weight="fill" /> {offer.discount_pct}% off · direct booking
          </span>
          <h2 className="text-xl font-bold text-slate-800">{offer.property_name}</h2>
          {offer.property_city && <p className="text-xs text-slate-400 mt-0.5">{offer.property_city}</p>}
          <p className="text-sm text-slate-500 mt-2">
            {offer.guest_name ? `Hi ${offer.guest_name.split(" ")[0]}, extend` : "Extend"} your stay for less than the booking sites.
          </p>
        </div>

        {unavailable ? (
          <div className="text-center bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-700">
            {offer.expired
              ? "This offer has expired. Please ask your host for a new link."
              : "There are no open nights right after your checkout to extend into."}
          </div>
        ) : (
          <>
            {/* Night picker */}
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">How many extra nights?</p>
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setNights(n => Math.max(1, n - 1))} disabled={nn <= 1}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-30 hover:bg-slate-50">
                  <Minus size={16} weight="bold" />
                </button>
                <div className="text-center w-24">
                  <div className="text-3xl font-bold text-slate-800">{nn}</div>
                  <div className="text-[11px] text-slate-400">night{nn > 1 ? "s" : ""}</div>
                </div>
                <button onClick={() => setNights(n => Math.min(maxN, n + 1))} disabled={nn >= maxN}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-30 hover:bg-slate-50">
                  <Plus size={16} weight="bold" />
                </button>
              </div>
              <p className="text-[11px] text-slate-400 text-center mt-2">Up to {maxN} night{maxN > 1 ? "s" : ""} available</p>
            </div>

            {/* Dates + price breakdown */}
            <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <CalendarBlank size={15} weight="bold" className="text-amber-500" />
                <span className="font-semibold">{fmt(offer.check_in)} → {fmt(checkout)}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500 pt-1 border-t border-slate-200">
                <span>Booking-site price</span>
                <span className="line-through text-slate-400">{inr(origTotal)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Your direct price ({nn} × {inr(offer.nightly_rate)})</span>
                <span className="font-semibold text-emerald-600">{inr(subtotal)}</span>
              </div>
              {gst > 0 && (
                <div className="flex justify-between text-xs text-slate-500"><span>GST</span><span>{inr(gst)}</span></div>
              )}
              <div className="flex justify-between font-bold text-slate-800 border-t border-slate-200 pt-2 text-base">
                <span>Total</span><span className="text-amber-600">{inr(total)}</span>
              </div>
              {savings > 0 && (
                <div className="text-center text-[11px] font-semibold text-emerald-600 bg-emerald-50 rounded-lg py-1.5">
                  You save {inr(savings)} vs booking sites
                </div>
              )}
            </div>

            {msg && (
              <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                <Warning size={13} weight="fill" />{msg}
              </div>
            )}

            <button onClick={reserveAndPay} disabled={paying}
              className="w-full py-3.5 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm">
              {paying
                ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <><CurrencyInr size={16} weight="bold" /> Pay {inr(total)} & Extend</>}
            </button>

            {offer.host_upi_id && (
              <p className="text-[11px] text-slate-400 text-center">
                Prefer UPI? Pay {inr(total)} to <span className="font-mono font-semibold text-slate-600">{offer.host_upi_id}</span> and message your host.
              </p>
            )}
          </>
        )}
        <p className="text-center text-[11px] text-slate-300">
          Powered by <span className="font-bold"><span className="text-slate-400">Host</span><span className="text-amber-500">Folio</span></span>
        </p>
      </motion.div>
    </div>
  );
}
